import { useEffect, useState } from 'react';

import useDebounceCallback from '@lib/core/src/hooks/useDebounceCallback';
import { Stack } from '@mui/material';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import { GoalDto } from '@/api/generated';
import EditableMarkdown from '@/components/common/EditableTextMarkdown';
import { getYearAndQuarter } from '@/utils/dateUtil';
import { actionToast } from '@/utils/toast';

import useGeneratePlan from '../hooks/useGeneratePlan';
import useStrategicPlan from '../hooks/useStrategicPlan';
import GeneratingStrategicPlan from './GeneratingStrategicPlan';
import {
  Container,
  PlaceHolder,
  BackgroundRow,
  GenerateButton,
  GenerateButtonText,
  RegenerateButton
} from './StrategicPlanStyles';

interface StrategicPlanProps {
  selectedPeriod: { year: number; quarter: number };
  goals: GoalDto[];
  monthToStart: number;
}

const DEBOUNCE_TIME = 700;

const StrategicPlan = ({ selectedPeriod, goals, monthToStart }: StrategicPlanProps) => {
  const { captureEvent } = useAnalytics();
  const {
    strategicPlan,
    onEditStrategicPlan,
    createStrategicPlan,
    isLoadingStrategicPlans,
    refetchGoals
  } = useStrategicPlan(selectedPeriod.year, selectedPeriod.quarter);
  const {
    generateStrategicPlan,
    isGenerating,
    generatedStrategicPlan,
    resetGenerateMutation,
    isGenerateError
  } = useGeneratePlan();

  const goalsTitles = goals
    .slice(0, 3)
    .map(goal => goal.title)
    .filter(Boolean) as string[];

  const [localPlan, setLocalPlan] = useState('');

  const handleEdit = (content: string) => {
    setLocalPlan(content);
  };

  const handleSave = () => {
    if (strategicPlan?.id) {
      onEditStrategicPlan(localPlan, strategicPlan?.id);
    }
  };

  useDebounceCallback(localPlan, DEBOUNCE_TIME, handleSave);

  useEffect(() => {
    if (!isLoadingStrategicPlans && !strategicPlan) {
      createStrategicPlan(selectedPeriod.year, selectedPeriod.quarter);
    }
  }, [
    isLoadingStrategicPlans,
    strategicPlan,
    createStrategicPlan,
    selectedPeriod.year,
    selectedPeriod.quarter
  ]);

  useEffect(() => {
    resetGenerateMutation();
  }, [selectedPeriod.year, selectedPeriod.quarter, resetGenerateMutation]);

  useEffect(() => {
    if (generatedStrategicPlan && strategicPlan?.id) {
      onEditStrategicPlan(generatedStrategicPlan, strategicPlan?.id!);
    }
  }, [generatedStrategicPlan, onEditStrategicPlan, strategicPlan]);

  const generatePlan = async () => {
    const res = await refetchGoals();
    const goals = res.data?.items || [];

    const goalsTitles = goals
      .filter(goal => {
        const quarterYear = getYearAndQuarter(new Date(goal.dueDate || ''), monthToStart);
        return (
          quarterYear.year === selectedPeriod.year && quarterYear.quarter === selectedPeriod.quarter
        );
      })
      .map(goal => goal.title) as string[];
    generateStrategicPlan([goalsTitles.join(', ')]);
  };

  const handleRegeneratePlan = () => {
    captureEvent(EVENTS.PLAN_REGENERATED);
    generatePlan();
  };

  const handleGeneratePlan = () => {
    captureEvent(EVENTS.PLAN_GENERATED);
    generatePlan();
  };

  useEffect(() => {
    if (isGenerateError) {
      actionToast({
        caption: 'There has been an error generating the strategic plan.',
        action: { label: 'Regenerate', onClick: handleRegeneratePlan },
        vertical: 'bottom'
      });
    }
  }, [isGenerateError, handleRegeneratePlan]);

  const showPlaceholder =
    (!isLoadingStrategicPlans &&
      !strategicPlan?.plan &&
      !isGenerating &&
      !generatedStrategicPlan) ||
    isGenerateError;

  return (
    <Container>
      {showPlaceholder && (
        <PlaceHolder>
          <BackgroundRow sx={{ width: '50%', marginBottom: '20px' }} />
          {Array.from({ length: 30 }).map((_, index) => (
            <BackgroundRow key={index} />
          ))}
          <GenerateButton onClick={handleGeneratePlan} disabled={goalsTitles.length === 0}>
            <GenerateButtonText>Generate strategic plan</GenerateButtonText>
          </GenerateButton>
        </PlaceHolder>
      )}
      {(strategicPlan?.plan || generatedStrategicPlan) && !isGenerating && !isGenerateError && (
        <Stack>
          <RegenerateButton onClick={handleRegeneratePlan}>
            <GenerateButtonText>Regenerate</GenerateButtonText>
          </RegenerateButton>
          <EditableMarkdown
            style={{ height: '100%', width: '100%' }}
            value={generatedStrategicPlan || strategicPlan?.plan || ''}
            callback={handleEdit}
          />
        </Stack>
      )}
      {isGenerating && <GeneratingStrategicPlan />}
    </Container>
  );
};

export default StrategicPlan;
